import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';
import React, { ReactElement } from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    BetLegMarketName,
    BetLegSelectionName,
} from 'src/domains/players/webview/components/Account/betHistoryTab/betsList/betsList/Bet.style';
import { BetHistoryLegType } from 'src/domains/players/webview/components/Account/betHistoryTab/BetHistoryTab.state';
import { DateTime } from 'src_common/utils/time/time';
import { useCommon } from 'src/domains/common/Common';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';

export const BetLegTitle = observer<{ leg: BetHistoryLegType; isSingle: boolean }>(
    'BetLegTitle',
    ({ leg, isSingle }): ReactElement | null => {
        const common = useCommon();
        const appSportsBookState = AppSportsBookState.get(common);
        const languagesState = LanguagesState.get(common);
        const { models } = appSportsBookState;

        if (leg.type === 'rab' || leg.type === 'betBuilder') {
            const selections = leg.selections ?? null;

            if (selections === null) {
                console.error('This branch should never occur (bet1)');
            } else {
                if (leg.sport?.id === 'horseracing') {
                    const label = selections.map((item) => `${item.market.name} | ${leg.event.name}`).join(' & ');
                    const selectionName = selections[0]?.selection.name ?? leg.event.name;

                    return (
                        <>
                            <BetLegSelectionName
                                isSingle={isSingle}
                                data-test='event-name'
                            >
                                {selectionName}
                            </BetLegSelectionName>
                            <BetLegMarketName data-test='market-name'>{label}</BetLegMarketName>
                        </>
                    );
                }

                const label = selections.map((item) => `${item.selection.name} | ${item.market.name}`).join(' & ');

                return (
                    <>
                        <BetLegSelectionName
                            isSingle={isSingle}
                            data-test='event-name'
                        >
                            {leg.event.name}
                        </BetLegSelectionName>
                        <BetLegMarketName data-test='market-name'>{label}</BetLegMarketName>
                    </>
                );
            }
        } else {
            const selectionId = leg.selection?.id ?? null;
            const selectionName = leg.selection?.name ?? null;
            if (selectionName === null) {
                return null;
            }

            const marketIdNumber = leg.market?.id ?? null;
            const marketName = leg.market?.name ?? null;
            if (marketName === null) {
                return null;
            }

            if (marketIdNumber === null) {
                return null;
            }

            // eslint-disable-next-line no-restricted-globals
            const eventIdRaw = Number(leg.event.id ?? leg.event.eventId);
            const eventId = models.id.getEventIdOption(isNaN(eventIdRaw) ? null : eventIdRaw);

            if (eventId === null) {
                return null;
            }

            const marketId = eventId.getMarketId(marketIdNumber);

            if (marketName === 'Trap Challenge') {
                const timeSettingsStartTime = eventId.getEventModel()?.timeSettingsStartTime;
                const trapChallengeCompetitionDay = DateTime.from(timeSettingsStartTime)?.format('DD MMM YYYY');

                return (
                    <>
                        <BetLegSelectionName
                            isSingle={isSingle}
                            data-test='selection-name'
                        >
                            {languagesState.showSelectionTranslation(marketId, selectionId, selectionName)}
                        </BetLegSelectionName>
                        <BetLegMarketName>
                            <span data-test='market-name'>
                                {languagesState.showMarketTranslation(marketId, marketName)}
                            </span>{' '}
                            | <span data-test='event-name'>{leg.event.name}</span>
                        </BetLegMarketName>
                        <div>{trapChallengeCompetitionDay}</div>
                    </>
                );
            } else {
                return (
                    <>
                        <BetLegSelectionName
                            isSingle={isSingle}
                            data-test='selection-name'
                        >
                            {languagesState.showSelectionTranslation(marketId, selectionId, selectionName)}
                        </BetLegSelectionName>

                        <BetLegMarketName>
                            <span data-test='market-name'>
                                {languagesState.showMarketTranslation(marketId, marketName)}
                            </span>{' '}
                            | <span data-test='event-name'>{leg.event.name}</span>
                        </BetLegMarketName>
                    </>
                );
            }
        }

        return null;
    }
);
